import { useEffect } from 'react';
import { useStickToBottomContext } from 'use-stick-to-bottom';

// Allowance for sub-pixel rounding when comparing the scroll offset with the end
const END_TOLERANCE_PX = 2;

/**
 * Keeps the end of the conversation in view when its scroll area gets shorter, as it does
 * when the keyboard slides in on a phone. The browser keeps the scroll offset through the
 * change, which would leave the end hidden. Applies only when the end was in view, so a
 * reader further up keeps their place. Works from the geometry rather than the library's
 * at-end flag, which an in-between layout of the change can reset.
 */
export const useKeepEndInView = () => {
  const { scrollRef, scrollToBottom } = useStickToBottomContext();

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    let lastHeight = scroller.clientHeight;
    const observer = new ResizeObserver(() => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      // The offset survives the change, so offset plus old height says where the view was
      const endWasInView = scrollTop + lastHeight >= scrollHeight - END_TOLERANCE_PX;
      const shrank = clientHeight < lastHeight;
      lastHeight = clientHeight;
      if (shrank && endWasInView) void scrollToBottom('instant');
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scrollRef, scrollToBottom]);
};
